import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus } from 'lucide-react';
import { FollowButton } from './FollowButton';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Follower {
  id: string;
  follower_id: string;
  profiles: {
    id: string;
    display_name: string;
    avatar_url: string;
    username: string;
    is_verified: boolean;
  };
}

interface FollowersListProps {
  userId: string;
}

export const FollowersList = ({ userId }: FollowersListProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFollowers();

    // Real-time subscription
    const channel = supabase
      .channel(`followers:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'follows',
          filter: `following_id=eq.${userId}`
        },
        () => {
          loadFollowers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const loadFollowers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('follows')
      .select(`
        id,
        follower_id,
        profiles!follows_follower_id_fkey(
          id,
          display_name,
          avatar_url,
          username,
          is_verified
        )
      `)
      .eq('following_id', userId);

    if (!error && data) {
      setFollowers(data as any);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="clean-card">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading fans...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="clean-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Fans ({followers.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {followers.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-muted-foreground">No fans yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {followers.map((follower) => (
              <div
                key={follower.id}
                className="flex items-center gap-3 p-3 border rounded-xl bg-white hover-lift cursor-pointer"
                onClick={() => navigate(`/user/${follower.profiles.id}`)}
              >
                <Avatar className="border-2 border-primary/10">
                  <AvatarImage src={follower.profiles.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {follower.profiles.display_name?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground flex items-center gap-1">
                    {follower.profiles.display_name}
                    {follower.profiles.is_verified && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">@{follower.profiles.username}</p>
                </div>
                {user && user.id !== follower.profiles.id && (
                  <FollowButton userId={follower.profiles.id} size="sm" />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
